import { countBy, minBy, sumBy } from "lodash-es";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { web } from "@kikoff/proto/src/protos";
import { webRPC } from "@kikoff/proto/src/rpc";
import { partition } from "@kikoff/utils/src/array";
import { msIn } from "@kikoff/utils/src/date";
import { serverNow } from "@kikoff/utils/src/number";
import {
  handleFailedStatus,
  handleProtoStatus,
  protoTime,
} from "@kikoff/utils/src/proto";

import { AppThunk, RootState } from "@store";
import analytics from "@util/analytics";

import { createLoadableSelector } from "../utils";

import { fetchUser } from "./user";

const initialState = {
  open: [] as Loan[],
  closed: [] as Loan[],
  offer: null as Loan.Offer[],
};

export type LendingState = typeof initialState;

const lendingSlice = createSlice({
  name: "lending",
  initialState,
  reducers: {
    updateLendingState(
      state,
      { payload }: PayloadAction<Partial<LendingState>>
    ) {
      Object.assign(state, payload);
    },
  },
});

export const { updateLendingState } = lendingSlice.actions;
export default lendingSlice.reducer;

declare namespace selectLoanBy {
  export interface Options {
    includeClosed?: boolean;
  }
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
const selectLoanBy = <Prop extends keyof Loan>(
  field: Prop,
  matches: Loan[Prop][],
  { includeClosed = false }: selectLoanBy.Options = {}
) => (state: RootState) =>
  [
    ...(state.loans.open || []),
    ...((includeClosed && state.loans.closed) || []),
  ]
    .sort((a, b) => b.openedAt.seconds - a.openedAt.seconds)
    .find((loan) => matches.includes(loan[field]));

export { selectLoanBy };

export const selectCbsLoan = (options: selectLoanBy.Options = {}) => {
  return selectLoanBy(
    "loanType",
    [
      web.public_.Loan.LoanType.PREMIUM,
      web.public_.Loan.LoanType.CREDIT_BUILDER_SAVING,
    ],
    options
  );
};

export const selectCbsOffer = createLoadableSelector(
  () => (state: RootState) =>
    state.loans.offer?.find(
      ({ loanType }) =>
        loanType === web.public_.Loan.LoanType.CREDIT_BUILDER_SAVING
    ),
  { loadAction: () => updateEligibleLoans() }
);

export const selectUnsecuredPersonalOffer = createLoadableSelector(
  () => (state: RootState) =>
    state.loans.offer?.find(
      ({ loanType }) =>
        loanType === web.public_.Loan.LoanType.UNSECURED_PERSONAL
    ),
  { loadAction: () => updateEligibleLoans() }
);

export const selectUnsecuredPersonalLoan = (
  options: selectLoanBy.Options = {}
) =>
  selectLoanBy(
    "loanType",
    [web.public_.Loan.LoanType.UNSECURED_PERSONAL],
    options
  );
export const selectLoan = (loanToken: string) => (state: RootState) =>
  [...(state.loans.open || []), ...(state.loans.closed || [])].find(
    (loan) => loan.token === loanToken
  );

export const selectLoanOrOffer = (tokenOrIdentifier: string) => (
  state: RootState
) =>
  [
    ...(state.loans.open || []),
    ...(state.loans.closed || []),
    ...(state.loans.offer || []),
  ].find(
    (loan) =>
      ("token" in loan
        ? loan.token
        : (loan as web.public_.ILoanOffer).identifier) === tokenOrIdentifier
  );

export const selectWithdrawableCbl = () => (state: RootState) =>
  state.loans.closed.find(
    (loan) =>
      loan.allowWithdrawal &&
      (loan.loanType === web.public_.Loan.LoanType.CREDIT_BUILDER_SAVING ||
        loan.loanType === web.public_.Loan.LoanType.PREMIUM)
  );

export const updateLoans = (
  openLoans: web.public_.ILoan[],
  closedLoans: web.public_.ILoan[]
): AppThunk => (dispatch) => {
  // NOTE: only doing this to fix bug where open loan is not most recent loan, to be fixed in SolidJS migration
  // in edge cases a user's most recent cbl is closed but they have a past open cbl, leaving closed empty here
  // ensures that the users oldest active cbl loan will always be the one displayed on pages that use selectCbsLoan({ includeClosed: true })
  // ensure other loans (unsecured) are displayed either way
  const [cbls, other] = partition(
    openLoans,
    ({ loanType }) =>
      loanType === web.public_.Loan.LoanType.CREDIT_BUILDER_SAVING
  );
  const oldestOpenCbl = minBy(cbls, (loan) => loan.openedAt.seconds);
  const closed = oldestOpenCbl
    ? closedLoans.filter(
        ({ openedAt, loanType }) =>
          openedAt.seconds < oldestOpenCbl.openedAt.seconds ||
          loanType !== web.public_.Loan.LoanType.CREDIT_BUILDER_SAVING
      )
    : closedLoans;
  dispatch(
    updateLendingState({
      closed,
      open: [oldestOpenCbl, ...other].filter(Boolean),
    })
  );
};

export const updateEligibleLoans = (): AppThunk<
  Promise<web.public_.ILoanOffer[]>
> => (dispatch) => {
  return webRPC.Lending.getEligibleLoans({}).then((data) => {
    dispatch(updateLendingState({ offer: data.loanOffers }));
    return data.loanOffers;
  });
};

export const payLoanInstallment = (
  request: Required<web.public_.IMakePaymentRequest>
): AppThunk<Promise<void>> => () => {
  return webRPC.Lending.makePayment(request).then(
    handleProtoStatus({
      SUCCESS() {
        analytics.convert("Make Loan Payment");
      },
      _DEFAULT: handleFailedStatus("Failed to pay loan installment"),
    })
  );
};

export const closeLoan = ({
  loanToken,
  closureReason,
}): AppThunk<Promise<void>> => (dispatch) =>
  webRPC.Lending.closeLoan({
    loanToken,
    closureReason,
  }).then(
    handleProtoStatus({
      SUCCESS() {
        dispatch(fetchUser.loans());
      },
      _DEFAULT: handleFailedStatus("Failed to close. Please contact support."),
    })
  );

export type Loan = web.public_.ILoan;
export namespace Loan {
  export const totalCost = (loan: Loan) =>
    loan.duration * loan.monthlyPaymentCents;
  export const storedSavingsCents = (loan: Loan) =>
    loan.closedAt
      ? loan.savingsVoucher?.valueCents
      : Math.max(
          sumBy(loan.loanInstallments, ({ paidAmountCents }) =>
            Math.max(paidAmountCents - loan.monthlyFeeCents, 0)
          ),
          0
        );
  export const installments = {
    paidCount: (loan: Loan) => countBy(loan.loanInstallments, "paid"),
    adminFee: (loan: Loan) =>
      loan.loanType === web.public_.Loan.LoanType.PREMIUM &&
      !countBy(loan.loanInstallments, "paid").true
        ? loan.adminFeeCents
        : 0,
  };

  export type Offer = web.public_.ILoanOffer;
  export namespace Offer {
    export const totalCost = (offer: Offer) =>
      offer.duration * offer.monthlyPaymentCents;
    export const totalFees = (offer: Offer) =>
      offer.monthlyFeeCents * offer.duration + offer.adminFeeCents;
  }
}

export namespace PersonalLoan {
  export const amountCentsRange = (() => {
    // temprarily set the lower end of the range to 600, legal min is really 501 but weird to show that
    const low = 600_00;
    const high = 3000_00;
    return Object.assign([low, high] as const, { low, high });
  })();

  export const disbursementComplete = (loan: Loan) =>
    Date.now() > loan.openedAt.seconds * msIn.second + 5 * msIn.day;

  export const interestPaid = (loan: Loan) =>
    sumBy(loan.loanInstallments, (installment) => {
      const interest =
        installment.paymentAmountCents -
        installment.paymentPrincipalAmountCents;

      return Math.min(interest, installment.paidAmountCents);
    });

  export const overdueTotal = (loan: Loan) =>
    loan.loanInstallments
      .filter(({ billingEnd, paidAt }) => {
        return protoTime(billingEnd) < serverNow() && !paidAt;
      })
      .reduce(
        (acc, installment) =>
          acc + (installment.paymentAmountCents - installment.paidAmountCents),
        0
      );

  export const amountDueTotal = (loan: Loan) =>
    loan.loanInstallments
      .filter(({ billingEnd, paidAt, billingStart }) => {
        return (
          (protoTime(billingEnd) < serverNow() ||
            (serverNow() < protoTime(billingEnd) &&
              serverNow() >= protoTime(billingStart))) &&
          !paidAt
        );
      })
      .reduce(
        (acc, installment) =>
          acc + (installment.paymentAmountCents - installment.paidAmountCents),
        0
      );

  export const maximumCustomAmount = (loan: Loan) => {
    const amountDueTotal = PersonalLoan.amountDueTotal(loan);
    return (
      amountDueTotal +
      loan.loanInstallments
        .filter(({ paidAt, billingEnd }) => {
          return !paidAt && protoTime(billingEnd) > serverNow();
        })
        .reduce(
          (acc, installment) => acc + installment.paymentPrincipalAmountCents,
          0
        )
    );
  };

  export function formatStringMoney(str: string) {
    return +str.replace(/[^0-9.]/g, "") * 100;
  }

  export function formatMoney(str: string) {
    const numberStr = (() => {
      // Prevent multiple decimals
      const [before, after] = str.split(".", 2);
      return `${before}${after != null ? `.${after}` : ""}`.replace(
        /[^0-9.]/g,
        ""
      );
    })();
    const number = +numberStr;

    if (!numberStr.includes(".") || numberStr.endsWith(".0"))
      return `$${number}`;
    if (numberStr.endsWith(".") || numberStr.endsWith(".00"))
      return `$${number}.00`;

    const [before, after] = numberStr.split(".");
    return `$${before}.${`${+after}`.slice(0, 2).padStart(2, "0")}`;
  }

  export const validCustomAmount = (
    amountDueTotal: number,
    customPayAmountString: string
  ) =>
    Math.min(500, amountDueTotal) <= formatStringMoney(customPayAmountString) &&
    formatStringMoney(customPayAmountString) <= amountDueTotal;
}
